<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from "vue-i18n";
import { useInvalidCardOverlay } from "@/store/overlays/invalid-card-overlay";

const { t } = useI18n();
const store = useInvalidCardOverlay();
const message = computed((): string => {
  const msg = store.get_error_type;
  return msg !== null ? t(msg) : "";
});
</script>

<template>
  <v-overlay
    v-model="store.showing"
    persistent
    no-click-animation
    color="error"
    z-index="920"
    class="align-center justify-center"
    style="background-color: rgba(232, 62, 54, 0.4); width: 100vw;"
  >
    <v-container fill-height fluid>
      <v-col class="align-center justify-center">
        <v-row class="justify-center">
          <v-card :class="`mx-4 justify-center`">
            <v-card-title class="bg-primary text-white text-center">
              {{ t("title") }}
            </v-card-title>
            <v-card-text
              class="text-black whitespace-pre text-center text-wrap my-2 py-0"
              width="90%"
            >
              {{ message }}
            </v-card-text>
            <v-card-actions class="justify-center">
              <!-- <v-row class="justify-center align-center"> -->
              <v-btn
                class="bg-success"
                depressed
                @click="store.hide"
              >
                <span>{{ t("close") }}</span>
              </v-btn>
              <!-- </v-row> -->
            </v-card-actions>
          </v-card>
        </v-row>
      </v-col>
    </v-container>
  </v-overlay>
</template>

<i18n lang="json5">
{
  "en": {
    "title": "Error",
    "somethingWentWrong": "Something went wrong...",
    "chargeError": "An error occurred during charging...\nPress the button below to release your cable",
    "apologize": "We apologize for the inconvenience.",
    "unauthorizedStation": "You're not authorized to charge on this station.",
    "loginLogout": "Try to logout and login again if you think this is a bug.",
    "invalid_number": "The card number entered is invalid or incomplete.",
    "invalid_cvc": "The CVC entered is invalid or incomplete.",
    "invalid_expiry_date": "The expiry date entered is invalid or incomplete.",
    "incorrect_zip": "The ZIP you entered is incorrect or incomplete",
    "card_declined": "The card has been declined",
    "expired_card": "Your card is expired.\nPlease try with another card.",
    "payment_intent_authentication_failure": "The authentication of your payment method failed.\nPlease choose a different payment method and try again.",
    "timeout": "Cannot reach our servers:\ncheck your internet connection",
    "bad_coupon": "Your coupon does not exist or is no longer active",
    "generic_error": "Something went wrong. Please check the credentials you entered are correct.",
    "close": "Close"
  },
  "it": {
    "error": "Errore",
    "somethingWentWrong": "C'é stato un errore...",
    "chargeError": "È avvenuto un errore nella ricarica...\nCliccare sul bottone qui sotto per liberare il cavo.",
    "apologize": "Ci spiace molto per l'inconveniente.",
    "unauthorizedStation": "Non sei autorizzato a caricare su questa colonnina",
    "loginLogout": "Prova a effettuare un logout e successivamente un login se pensi che si tratti di un bug.",
    "invalid_number": "Il numero di carta inserito è errato o incompleto.",
    "invalid_cvc": "Il CVC inserito è errato o incompleto.",
    "invalid_expiry_date": "La data di scadenza inserita è errata o incompleta.",
    "incorrect_zip": "Il CAP inserito è errato o incompleto.",
    "card_declined": "La carta è stata rifiutata.",
    "expired_card": "La carta è scaduta.\nProva con un'altra carta per procedere al pagamento.",
    "payment_intent_authentication_failure": "L'autenticazione del tuo metodo di pagamento è fallita.\nTi invitiamo a scegliere un altro metodo di pagamento e riprovare.",
    "timeout": "Impossibile contattare i server:\ncontrolla la tua connessione ad internet",
    "bad_coupon": "Il coupon non esiste o non è più attivo",
    "generic_error": "Qualcosa è andato storto. Verifica che le credenziali inserite siano corrette.",
    "close": "Chiudi"
  },
  "fr": {
    "title": "Erreur",
    "somethingWentWrong": "Une erreur est survenue...",
    "chargeError": "Une erreur est survenue durant la charge... \n Cliquez sur le bouton ci-dessous pour libérer votre câble.",
    "apologize": "Veuillez nous excuser pour la gêne occasionnée.",
    "unauthorizedStation": "Vous n'êtes pas autorisé à utiliser cette station.",
    "loginLogout": "Essayez de vous déconnecter/reconnecter si vous pensez que vous devriez y avoir accès.",
    "invalid_number": "Le numéro de carte saisi est invalide ou incomplet.",
    "invalid_cvc": "Le CVC saisi est invalide ou incomplet.",
    "invalid_expiry_date": "La date d'expiration saisie est invalide ou incomplète.",
    "incorrect_zip": "Le code postal que vous avez saisi est incorrect ou incomplet",
    "card_declined": "La carte a été refusée",
    "expired_card": "Votre carte a expiré.\nVeuillez essayer avec une autre carte.",
    "payment_intent_authentication_failure": "L'authentification de votre mode de paiement a échoué. Veuillez choisir un autre mode de paiement et réessayer.",
    "timeout": "Ne peut pas atteindre nos serveurs,\nvérifiez votre connexion internet",
    "bad_coupon": "Votre coupon n'existe pas ou n'est plus actif",
    "generic_error": "Une erreur s'est produite.\nVeuillez vérifier que les informations d'identification que vous avez saisies sont correctes.",
    "close": "Fermer"
  },
  "de": {
    "title": "Fehler",
    "somethingWentWrong": "Da ist ein Fehler...",
    "chargeError": "Beim Laden ist ein Fehler aufgetreten",
    "apologize": "Wir entschuldigen uns sehr für die Unannehmlichkeiten.",
    "unauthorizedStation": "Sie sind nicht berechtigt, in diese Spalte hochzuladen",
    "loginLogout": "Versuchen Sie, sich abzumelden und dann wieder anzumelden, wenn Sie der Meinung sind, dass dies ein Fehler ist.",
    "invalid_number": "Die eingegebene Kartennummer ist falsch oder unvollständig.",
    "invalid_cvc": "Der eingegebene CVC ist falsch oder unvollständig.",
    "invalid_expiry_date": "Das eingegebene Ablaufdatum ist falsch oder unvollständig.",
    "incorrect_zip": "Die eingegebene Postleitzahl ist falsch oder unvollständig.",
    "card_declined": "Die Karte wurde abgelehnt.",
    "expired_card": "Die Karte ist abgelaufen\nVersuchen Sie es mit einer anderen Karte, um mit der Zahlung fortzufahren.",
    "payment_intent_authentication_failure": "Ihre Zahlungsauthentifizierung ist fehlgeschlagen\nBitte wählen Sie eine andere Zahlungsmethode und versuchen Sie es erneut.",
    "timeout": "Server können nicht kontaktiert werden\nPrüfe deine Internetverbindung",
    "bad_coupon": "Ihr Coupon existiert nicht oder ist nicht mehr aktiv",
    "generic_error": "Es ist etwas schief gelaufen. Überprüfen sie ob die Eingaben korrekt sind.",
    "close": "Schluss"
  },
  "nl": {
    "title": "Fout",
    "somethingWentWrong": "Er is iets fout gegaan...",
    "chargeError": "Er is iets fout gegaan tijdens het opladen... \nKlik op de onderstaande knop om uw kabel los te maken.",
    "apologize": "Wij verontschuldigen ons voor het ongemak.",
    "unauthorizedStation": "U mag dit station niet gebruiken.",
    "loginLogout": "Probeer af te melden/opnieuw aan te melden als u denkt dat u er toegang toe zou moeten hebben.",
    "invalid_number": "Het ingevoerde kaartnummer is ongeldig of onvolledig.",
    "invalid_cvc": "De ingevoerde CVC is ongeldig of onvolledig.",
    "invalid_expiry_date": "De ingevoerde vervaldatum is ongeldig of onvolledig.",
    "incorrect_zip": "De ingevoerde postcode is onjuist of onvolledig.",
    "card_declined": "De kaart is geweigerd.",
    "expired_card": "Uw kaart is verlopen.\nProbeer het met een andere kaart.",
    "payment_intent_authentication_failure": "De verificatie van uw betaalmethode is mislukt.\nKies een andere betaalmethode en probeer het opnieuw.",
    "timeout": "Kan onze servers niet bereiken,\ncontroleer uw internetverbinding",
    "bad_coupon": "Uw coupon bestaat niet of is niet langer actief",
    "generic_error": "Er is een fout opgetreden.\nControleer of de gegevens die u hebt ingevoerd correct zijn.",
    "close": "Sluiten"
  },
  "es": {
    "title": "Error",
    "somethingWentWrong": "Algo ha ido mal...",
    "chargeError": "Se ha producido un error durante la carga...\nPulse el botón de abajo para liberar su cable",
    "apologize": "Nos disculpamos por las molestias ocasionadas.",
    "unauthorizedStation": "No está autorizado a cargar en esta estación.",
    "loginLogout": "Intente cerrar sesión y volver a iniciarla si cree que esto es un error.",
    "invalid_number": "El número de tarjeta introducido es inválido o incompleto.",
    "invalid_cvc": "El CVC introducido es inválido o incompleto.",
    "invalid_expiry_date": "La fecha de caducidad introducida es inválida o incompleta.",
    "incorrect_zip": "El código postal introducido es incorrecto o incompleto.",
    "card_declined": "La tarjeta ha sido rechazada.",
    "expired_card": "Su tarjeta ha caducado.\nPor favor, intente con otra tarjeta.",
    "payment_intent_authentication_failure": "La autenticación de su método de pago ha fallado.\nPor favor, elija un método de pago diferente e inténtelo de nuevo.",
    "timeout": "No se pueden contactar nuestros servidores:\ncomprueba tu conexión a Internet",
    "bad_coupon": "Su cupón no existe o ya no está activo",
    "generic_error": "Algo ha ido mal. Por favor, compruebe que los datos introducidos son correctos.",
    "close": "Cerrar"
  }
}
</i18n>
<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
